import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { FieldArray, FormikProvider } from "formik";
import React, { useContext, useMemo, useState } from "react";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import AutoComplete from "../common/AutoComplete";
import { nameof } from "../common/ObjectUtils";
import Manipulate from "./AutocompleteManipulateValues";
import TransferReasonService from "./TransferReasonService";

interface Props {
  formik: any;
  navigateBack: () => void;
  setPage: (value: boolean) => void;
  handleNext: () => void;
  tenant: string;
  changeStartStation: boolean;
  changeTransportationType: boolean;
  weight: number;
  setWeight: (value: number) => void;
  updateDatasourceAcriss: () => void;
}

const VehicleAddPage = (props: Props) => {
  const manipulate = Manipulate();
  const [plates, setPlate] = useState<any>([]);
  const { setLoading, userLocations, allLocations } = useContext(ContextOperation);
  const snackbar = useSnackbar();
  const t = useOperationTranslation();
  const theme = useTheme();

  const disableButton = () => {
    const removeEmptyString = props.formik.values.plates.filter(
      (element: any) => element !== ""
    );
    if (removeEmptyString.length > 0 && props.weight <= 9) return false;
    else return true;
  };

  const closeModal = () => {
    props.setPage(false);
  };

  const calculateWeight = (value: string) => {
    let getWeight = plates.filter((x: any) => x.label === value);
    return getWeight[0]?.loadIndex;
  };

  useMemo(() => {
    manipulate
      .getPlates(props.formik.values.startStationId, props.formik.values.startStationType, props.tenant)
      .then((response: any) => setPlate(response));
  }, []);

  useMemo(() => {
    if (
      props.changeStartStation === true ||
      props.changeTransportationType === true
    ) {
      props.formik.setFieldValue(nameof<any>(`plates`), [""]);
      props.setWeight(0);
    }
  }, [props.formik.values.startStationId, props.formik.values.transferType]);

  const removeField = (index: any) => {
    props.formik.values.plates.splice(index, 1);
    if (props.formik.values.plates.length === 0) {
      props.formik.values.plates = [""];
    }
  };

  const createTransferRequest = () => {
    const arrayManipulateDrivers = props.formik.values.driversId.filter(Number);
    const arrayManipulatePlates = props.formik.values.plates.filter(
      (str: any) => str !== ""
    );
    const data = {
      ...props.formik.values,
      driversId: arrayManipulateDrivers,
      plates: arrayManipulatePlates,
      startStationType: userLocations.find(element => element.bpCode === props.formik.values.startStationId)?.typeEnum || null,
      expectedEndStationType: allLocations.find(element => element.bpCode === props.formik.values.expectedEndStationId)?.type || null,
      endStationType: allLocations.find(element => element.bpCode === props.formik.values.endStationId)?.type || null,
    };
    setLoading(true);
    TransferReasonService.put
      .createTransferReason(props.tenant, data)
      .then(() => {
        props.setPage(false);
        props.updateDatasourceAcriss();
        setLoading(false);
        snackbar.show("success", t("transferReason.messages.successCreate"));
      })
      .catch(() => {
        setLoading(false);
        snackbar.show("error", t("transferReason.messages.errorCreate"));
      });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "14px",
          color: "#18305B",
          lineHeight: "16px",
        }}
      >
        {t("transferReason.navigation.vehicleTitle")}
      </Box>
      <Grid container>
        <Grid item lg={7}>
          <FormikProvider value={props.formik}>
            <FieldArray
              name="plates"
              render={(arrayHelpers: any) => (
                <div>
                  <Grid container columns={12} rowGap={5.5} spacing={2}>
                    {props.formik.values.plates.map(
                      (element: any, index: number) => (
                        <>
                          <Grid item xl={5} lg={5}>
                            <AutoComplete
                              fullWidth
                              label={t("transferReason.add.plate")}
                              value={props.formik.values.plates[index]}
                              options={plates}
                              getOptionDisabled={(option: any) => {
                                if (
                                  props.formik.values.plates.some(
                                    (day: any) => day === option.label
                                  )
                                ) {
                                  return true;
                                }
                                return false;
                              }}
                              onChange={(value: any) => {
                                if (
                                  props.formik.values.transferType !== "DRIVER"
                                ) {
                                  if (value !== undefined) {
                                    let removeWeight = 0;
                                    if (
                                      props.formik.values.plates[index] !==
                                        "" &&
                                      props.formik.values.plates[index] !==
                                        value
                                    ) {
                                      removeWeight = calculateWeight(
                                        props.formik.values.plates[index]
                                      );
                                    }
                                    props.formik.setFieldValue(
                                      nameof<any>(`plates[${index}]`),
                                      value
                                    );
                                    props.setWeight(
                                      props.weight +
                                        calculateWeight(value) -
                                        removeWeight
                                    );
                                  } else {
                                    props.setWeight(
                                      props.weight -
                                        calculateWeight(
                                          props.formik.values.plates[index]
                                        )
                                    );
                                    removeField(index);
                                  }
                                } else {
                                  if (value === undefined) {
                                    props.formik.setFieldValue(
                                      nameof<any>(`plates[${index}]`),
                                      ""
                                    );
                                  } else
                                    props.formik.setFieldValue(
                                      nameof<any>(`plates[${index}]`),
                                      value
                                    );
                                }
                              }}
                            />
                          </Grid>

                          {props.formik.values.transferType !== "DRIVER" &&
                            props.weight < 9 && (
                              <>
                                {props.formik.values.plates.length - 1 ==
                                  index && (
                                  <Grid item xl={2} lg={2}>
                                    <Tooltip title={t("tooltipTitle.add")}>
                                      <IconButton
                                        color="accent"
                                        disabled={
                                          props.formik.values.plates[index] !==
                                          ""
                                            ? false
                                            : true
                                        }
                                        onClick={() => {
                                          arrayHelpers.push("");
                                        }}
                                      >
                                        <AddCircleOutlineIcon fontSize="large" />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                )}
                              </>
                            )}
                        </>
                      )
                    )}
                  </Grid>
                  {props.weight > 9 && (
                    <Box sx={{ color: "red", fontSize: "0.75rem" }}>
                      {t("transferReason.messages.errorWeight")}
                    </Box>
                  )}
                </div>
              )}
            />
          </FormikProvider>
        </Grid>
        <Grid item lg={12} direction="row">
          <Grid container direction="row">
            <Grid item lg={4}>
              {props.formik.values.transferType !== "DRIVER" && (
                <p>
                  {t("transferReason.add.weight")}: {props.weight}/9
                </p>
              )}
            </Grid>
            <Grid item lg={8}>
              <Grid container justifyContent="flex-end" spacing={2}>
                <Grid item>
                  <Button
                    sx={{
                      maxWidth: "246px",
                      maxHeight: "48px",
                      minWidth: "246px",
                      minHeight: "48px",
                    }}
                    variant="outlined"
                    onClick={
                      props.formik.values.id == ""
                        ? props.navigateBack
                        : closeModal
                    }
                  >
                    <Typography
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 700,
                        lineHeight: 2.25,
                        color: "#C8017D",
                      }}
                      variant="subtitle1"
                    >
                      {props.formik.values.id == ""
                        ? t("transferReason.navigation.back")
                        : t("actionButtons.cancel")}
                    </Typography>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    sx={{
                      maxWidth: "246px",
                      maxHeight: "48px",
                      minWidth: "246px",
                      minHeight: "48px",
                    }}
                    type="submit"
                    form="transfer-reason"
                    disabled={disableButton()}
                    variant="contained"
                    color="accent"
                    onClick={
                      props.formik.values.transferType === "DRIVER"
                        ? props.handleNext
                        : createTransferRequest
                    }
                  >
                    <Typography
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 700,
                        lineHeight: 2.25,
                        color: "white",
                      }}
                      variant="subtitle1"
                    >
                      {props.formik.values.transferType === "DRIVER"
                        ? t("transferReason.navigation.continue")
                        : t("transferReason.add.createTransferRequest")}
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default VehicleAddPage;
